/* Nagami PDP — interactive viewer + Shopify cart hooks.
 * Reads #product-data (serialised in main-product-nagami.liquid).
 * Posts to /cart/add.js on submit. Persists recently-viewed in localStorage. */

(() => {
  const dataEl = document.getElementById('product-data');
  if (!dataEl) return;
  const data = JSON.parse(dataEl.textContent);

  // Indices into v.options for the colour & size options. Either may be -1.
  const COLOR_IDX = typeof data.color_idx === 'number' ? data.color_idx : 0;
  const SIZE_IDX  = typeof data.size_idx  === 'number' ? data.size_idx  : -1;
  // Canonical "display" size (matches the server's size_display). Used to pick the
  // representative variant per colour. Works for any size naming, not just "S".
  const DISPLAY_SIZE = (typeof data.size_display === 'string' && data.size_display)
    ? data.size_display.trim().toLowerCase() : 's';

  const optColor = v => (v && v.options) ? v.options[COLOR_IDX] : undefined;
  const optSize  = v => (SIZE_IDX < 0 || !v || !v.options) ? null : v.options[SIZE_IDX];

  const initialVariantParam = new URLSearchParams(location.search).get('variant');
  const findVariant = id => data.variants.find(v => String(v.id) === String(id));

  // Unique colour list (one variant per colour, S-size preferred so swatch
  // image / shadows come from the small-size sample).
  function uniqueColors() {
    const map = new Map();
    data.variants.forEach(v => {
      const color = optColor(v);
      if (color == null) return;
      const size = optSize(v);
      const isS = typeof size === 'string' && size.trim().toLowerCase() === DISPLAY_SIZE;
      if (!map.has(color) || isS) map.set(color, v);
    });
    return Array.from(map.values());
  }

  // Unique ordered size list (preserves first-seen order from product.variants).
  function uniqueSizes() {
    if (SIZE_IDX < 0) return [];
    const seen = [];
    data.variants.forEach(v => {
      const s = optSize(v);
      if (s != null && !seen.includes(s)) seen.push(s);
    });
    return seen;
  }

  function pickVariantByOptions(color, size) {
    return data.variants.find(v => {
      if (optColor(v) !== color) return false;
      if (SIZE_IDX >= 0 && optSize(v) !== size) return false;
      return true;
    });
  }

  function availableSizesForColor(color) {
    if (SIZE_IDX < 0) return new Set();
    const sizes = new Set();
    data.variants.forEach(v => {
      if (optColor(v) === color && v.available) sizes.add(optSize(v));
    });
    return sizes;
  }

  const initialVariant = findVariant(initialVariantParam)
    || findVariant(data.current_id)
    || data.variants.find(v => v.available)
    || data.variants[0];

  const state = {
    variantId: initialVariant && initialVariant.id,
    viewIndex: 0,
    qty: 1,
  };

  const activeVariant = () => findVariant(state.variantId);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const RECENT_KEY = 'nagami.recentlyViewed';
  const RECENT_MAX = 8;
  const labels = Object.assign({
    add_to_cart: 'Add to cart',
    sold_out:    'Sold out',
    adding:      'Adding…',
    added:       'Added to cart',
    could_not:   'Could not add to cart',
  }, data.labels || {});

  const els = {
    viewer: document.querySelector('[data-viewer]'),
    stage: document.querySelector('[data-viewer-stage]'),
    vases: Array.from(document.querySelectorAll('[data-vase]')),
    current: document.querySelector('[data-viewer-current]'),
    total: document.querySelector('[data-viewer-total]'),
    prev: document.querySelector('[data-viewer-prev]'),
    next: document.querySelector('[data-viewer-next]'),
    swatches: document.querySelector('[data-viewer-swatches]'),
    swatchName: document.querySelector('[data-swatch-name]'),
    sizePicker: document.querySelector('[data-size-picker]'),
    dimSizes: document.querySelector('[data-dim-sizes]'),
    galleryHero: document.querySelector('[data-gallery-hero]'),
    galleryThumbs: document.querySelector('[data-gallery-thumbs]'),
    variantGalleries: document.querySelectorAll('[data-variant-gallery]'),
    sections: document.querySelector('[data-buy-sections]'),
    form: document.querySelector('[data-product-form]'),
    variantInput: document.querySelector('[data-variant-input]'),
    qtyInput: document.querySelector('[data-qty-input]'),
    qtyInc: document.querySelector('[data-qty-inc]'),
    qtyDec: document.querySelector('[data-qty-dec]'),
    cta: document.querySelector('[data-buy-add]'),
    ctaLabel: document.querySelector('.buy__cta-label'),
    price: document.querySelector('[data-price]'),
    priceSale: document.querySelector('[data-price-sale]'),
    priceCompare: document.querySelector('[data-price-compare]'),
    cartCount: document.querySelector('[data-cart-count]'),
    rowArrows: document.querySelectorAll('[data-row-prev],[data-row-next]'),
    related: document.querySelector('[data-recommendations]'),
    recent: document.querySelector('[data-recently-viewed]'),
    toast: document.querySelector('[data-toast]'),
    // The Concept theme's variant picker (in the sibling featured-product section).
    // Two-way sync: we listen for its `change` events and we drive it back when
    // the user picks colour / size in the Nagami section.
    conceptPicker: document.querySelector('variant-selects'),
  };

  // Re-entrancy guard for the Nagami ↔ Concept variant-picker sync.
  let syncingToConcept = false;

  function init() {
    renderSwatches();
    bindGalleryThumbs();
    bindVariantGalleryThumbs();
    bindControls();
    bindConceptSync();
    bindForm();
    paintViewer({ animate: false });
    paintVariantGallery();
    if (els.related) loadRecommendations();
    if (els.recent) loadRecentlyViewed();
    pushRecentlyViewed();
  }

  // A variant is "navigable" when its vase image exists in the DOM (rendered by Liquid).
  function navigableVariantIds() {
    return els.vases.map(el => el.dataset.variantId);
  }
  // Display variants = one per colour (S-size when present). Used by the colour
  // picker and the carousel — the actual underlying cart variant may differ
  // (depending on the currently-selected size).
  function displayVariants() {
    return uniqueColors();
  }
  function hasVase(variantId) {
    return els.vases.some(el => el.dataset.variantId === String(variantId));
  }
  // The carousel renders the S-size vase per colour. When the user is on an M
  // variant, the "displayed" variant id is the S-size sibling of the same colour.
  function displayVariantFor(variant) {
    if (!variant) return null;
    const color = optColor(variant);
    return uniqueColors().find(v => optColor(v) === color) || variant;
  }

  function paintViewer({ animate = true } = {}) {
    const v = activeVariant();
    if (!v) return;

    if (v.ambient) document.documentElement.style.setProperty('--ambient', v.ambient);

    // Carousel center is keyed off the DISPLAY variant (the S-size sibling of
    // the active colour), so picking M doesn't desync the vase.
    const displayId = (displayVariantFor(v) || v).id;
    const navigableIds = navigableVariantIds();
    const N = navigableIds.length;
    const centerIdx = navigableIds.indexOf(String(displayId));
    const total = N;

    if (els.current) els.current.textContent = String(Math.max(centerIdx, 0) + 1).padStart(2, '0');
    if (els.total)   els.total.textContent   = String(total).padStart(2, '0');

    // Assign role to every vase element based on its position relative to the center.
    els.vases.forEach(el => {
      const vid = el.dataset.variantId;
      const i = navigableIds.indexOf(vid);
      if (i === -1) { el.dataset.role = 'off-right'; return; }
      if (i === centerIdx) { el.dataset.role = 'center'; return; }
      const leftIdx  = (centerIdx - 1 + N) % N;
      const rightIdx = (centerIdx + 1) % N;
      if (i === leftIdx)  { el.dataset.role = 'left';  return; }
      if (i === rightIdx) { el.dataset.role = 'right'; return; }
      // Park off-stage on the side closest to its travel direction.
      const forward = (i - centerIdx + N) % N;
      el.dataset.role = forward < N / 2 ? 'off-right' : 'off-left';
    });

    // Safari/iOS sometimes keeps a stale compositor layer when the data-role
    // attribute changes (the new role's filter rule doesn't paint until the
    // next animation frame, so the central vase stays blurred). Force a
    // synchronous style/layout flush to invalidate the layer cache.
    if (els.stage) void els.stage.offsetWidth;

    if (els.prev) els.prev.disabled = total <= 1;
    if (els.next) els.next.disabled = total <= 1;

    const currentColor = optColor(v);
    const currentSize  = optSize(v);

    // Expose the selected size on the viewer element so CSS can scale the
    // centre vase (e.g. 20% larger when M is selected). The editor's
    // "Preview at M scale" checkbox sets data-preview-locked, which pins
    // data-size to whatever the server rendered (M) so the slider's effect
    // stays visible regardless of which variant the JS resolves on reload.
    if (els.viewer && !els.viewer.hasAttribute('data-preview-locked')) {
      els.viewer.dataset.size = currentSize ? String(currentSize).trim().toUpperCase() : '';
    }

    if (els.swatchName) els.swatchName.textContent = currentColor || '';
    // Keep the duplicate gallery row's colour-name label in sync too.
    document.querySelectorAll('[data-gallery-swatch-name]').forEach(el => {
      el.textContent = currentColor || '';
    });
    // Swatch aria-checked: match on colour (each swatch has data-color); we
    // also keep the legacy data-variant attribute for back-compat.
    document.querySelectorAll('.swatch').forEach(el => {
      const matches = el.dataset.color
        ? el.dataset.color === currentColor
        : el.dataset.variant === String(displayId);
      el.setAttribute('aria-checked', matches ? 'true' : 'false');
    });

    // Size pills: aria-checked matches current size; disabled when that size
    // doesn't exist in the currently-selected colour.
    if (els.sizePicker && SIZE_IDX >= 0) {
      const availSizes = availableSizesForColor(currentColor);
      els.sizePicker.querySelectorAll('.size-pill').forEach(btn => {
        const s = btn.dataset.size;
        btn.setAttribute('aria-checked', s === currentSize ? 'true' : 'false');
        btn.disabled = !availSizes.has(s);
      });
    }

    // Per-size measurement rows above the diagram: aria-current="true" for the
    // row whose label matches the selected size (case-insensitive).
    if (els.dimSizes && currentSize) {
      const cs = String(currentSize).trim().toLowerCase();
      els.dimSizes.querySelectorAll('.dim__size-row').forEach(row => {
        const rs = String(row.dataset.size || '').trim().toLowerCase();
        const match = rs === cs;
        row.classList.toggle('dim__size-row--current', match);
        if (match) row.setAttribute('aria-current', 'true');
        else       row.removeAttribute('aria-current');
      });
    }

    if (els.variantInput) els.variantInput.value = state.variantId;
    // Update sale + compare-at price independently
    if (els.priceSale && v.price_money) {
      els.priceSale.textContent = v.price_money;
      els.priceSale.classList.toggle('buy__price--sale', !!v.on_sale);
    } else if (els.price && v.price_money) {
      els.price.textContent = v.price_money;
    }
    if (els.priceCompare) {
      if (v.on_sale && v.compare_money) {
        els.priceCompare.textContent = v.compare_money;
        els.priceCompare.hidden = false;
      } else {
        els.priceCompare.textContent = '';
        els.priceCompare.hidden = true;
      }
    }
    if (els.cta) {
      els.cta.disabled = !v.available;
      if (els.ctaLabel) els.ctaLabel.textContent = v.available ? labels.add_to_cart : labels.sold_out;
    }

    // (carousel transitions happen via CSS on data-role changes; no manual image swap needed)

    // Keep the address bar clean: no variant id and no recommendation-tracking
    // params (pr_*). Other params (utm_*, etc.) are left untouched.
    const url = new URL(location.href);
    [...url.searchParams.keys()].forEach(k => { if (k === 'variant' || k.startsWith('pr_')) url.searchParams.delete(k); });
    history.replaceState({}, '', url);
  }

  function setVariant(id, { fromConcept = false } = {}) {
    if (String(id) === String(state.variantId)) return;
    state.variantId = id;
    state.viewIndex = 0;
    paintViewer({ animate: true });
    paintVariantGallery();
    if (!fromConcept) syncToConcept();
  }

  // Colour change keeps the current size when that combo exists; otherwise
  // falls back to the first available size for the new colour.
  function setColor(color) {
    const cur = activeVariant();
    const curSize = optSize(cur);
    let next = pickVariantByOptions(color, curSize);
    if (!next || !next.available) {
      const fallback = data.variants.find(v => optColor(v) === color && v.available)
        || data.variants.find(v => optColor(v) === color);
      next = fallback || next;
    }
    if (next) setVariant(next.id);
  }

  // Size change keeps the current colour; we trust the disabled state on the
  // pill to prevent users picking a size that doesn't exist for this colour.
  function setSize(size) {
    if (SIZE_IDX < 0) return;
    const cur = activeVariant();
    const next = pickVariantByOptions(optColor(cur), size);
    if (next) setVariant(next.id);
  }

  // Cycles between the unique colours (used by prev/next arrows + keyboard).
  function shiftVariant(dir) {
    const colors = uniqueColors();
    if (colors.length <= 1) return;
    const cur = activeVariant();
    const curColor = optColor(cur);
    const idx = colors.findIndex(v => optColor(v) === curColor);
    const target = colors[((idx < 0 ? 0 : idx) + dir + colors.length) % colors.length];
    setColor(optColor(target));
  }

  // Build a single swatch button for a colour variant. `bindName` controls
  // whether hover/focus updates the viewer's colour-name label (only the
  // viewer's own row drives that label).
  function makeSwatchButton(v, { bindName = true } = {}) {
    const color = optColor(v);
    const btn = document.createElement('button');
    btn.type = 'button';
    const isTranslucent = v.is_translucent === 'true' || v.is_translucent === true;
    const hasImage = !!v.swatch_image;
    const colorL = (color || '').toLowerCase();
    btn.className = 'swatch' +
      (hasImage ? ' swatch--has-image' : '') +
      (isTranslucent ? ' swatch--translucent' : '') +
      (colorL.includes('blanco') || colorL === 'white' ? ' swatch--white' : '');
    btn.dataset.color = color;
    btn.dataset.variant = String(v.id); // legacy
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-label', color);
    btn.setAttribute('aria-checked', color === optColor(activeVariant()) ? 'true' : 'false');
    if (hasImage) {
      btn.style.backgroundImage = `url("${v.swatch_image}")`;
    } else if (!isTranslucent && v.swatch) {
      btn.style.setProperty('--c', v.swatch);
    }
    // Clicking either row selects the colour; paintViewer() then re-syncs the
    // aria-checked state across EVERY .swatch on the page, so both rows stay
    // in lockstep automatically.
    btn.addEventListener('click', () => setColor(color));
    if (bindName && els.swatchName) {
      btn.addEventListener('mouseenter', () => { els.swatchName.textContent = color; });
      btn.addEventListener('mouseleave', () => { els.swatchName.textContent = optColor(activeVariant()) || ''; });
      btn.addEventListener('focus',      () => { els.swatchName.textContent = color; });
      btn.addEventListener('blur',       () => { els.swatchName.textContent = optColor(activeVariant()) || ''; });
    }
    return btn;
  }

  function fillSwatchRow(row, opts) {
    row.innerHTML = '';
    uniqueColors().forEach(v => {
      if (!hasVase(v.id)) return;
      row.appendChild(makeSwatchButton(v, opts));
    });
  }

  function renderSwatches() {
    if (els.swatches) fillSwatchRow(els.swatches, { bindName: true });
    renderGallerySwatches();
  }

  // Duplicate the colour swatches directly under the variant gallery so the
  // shopper can pick a colour there too. Selection is fully synced with the
  // viewer's row via setColor()/paintViewer(). Built in JS (and styled by
  // reusing the existing .viewer__swatches-row / .swatch classes) so the
  // markup-heavy product section doesn't need to change.
  function renderGallerySwatches() {
    const gallery = document.querySelector('[data-variant-gallery]');
    if (!gallery) return;
    let wrap = document.querySelector('[data-gallery-swatches-wrap]');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'variant-gallery__swatches';
      wrap.setAttribute('data-gallery-swatches-wrap', '');
      wrap.style.marginTop = '18px';
      // On mobile the two-column area flattens into one ordered flex stack;
      // inherit the gallery block's --block-order so this duplicate row stays
      // directly BELOW the variant gallery instead of jumping to the top.
      wrap.style.order = 'var(--block-order)';

      // Eyebrow word ("Colour"/"Color") is copied from the viewer's swatch
      // label, which Liquid already renders in the storefront language.
      const eyebrowSrc = document.querySelector('.viewer__swatches-eyebrow');
      const eyebrowText = eyebrowSrc ? eyebrowSrc.textContent : '';
      const label = document.createElement('div');
      label.className = 'viewer__swatches-label';
      label.style.marginTop = '12px'; // label sits BELOW the swatches now
      label.innerHTML =
        '<span class="viewer__swatches-eyebrow">' + eyebrowText + '</span> ' +
        '<span data-gallery-swatch-name></span>';

      const row = document.createElement('div');
      row.className = 'viewer__swatches-row';
      row.setAttribute('role', 'radiogroup');
      row.setAttribute('data-gallery-swatches', '');

      // Swatches first, colour-name label underneath.
      wrap.appendChild(row);
      wrap.appendChild(label);
      gallery.insertAdjacentElement('afterend', wrap);
    }
    fillSwatchRow(wrap.querySelector('[data-gallery-swatches]'), { bindName: false });
    const nameEl = wrap.querySelector('[data-gallery-swatch-name]');
    if (nameEl) nameEl.textContent = optColor(activeVariant()) || '';
  }

  function bindGalleryThumbs() {
    if (!els.galleryThumbs) return;
    els.galleryThumbs.querySelectorAll('li').forEach(li => {
      const img = li.querySelector('img');
      if (!img) return;
      li.addEventListener('click', () => {
        els.galleryHero.src = img.currentSrc || img.src;
        els.galleryHero.alt = img.alt;
        els.galleryThumbs.querySelectorAll('li').forEach(x => x.removeAttribute('aria-current'));
        li.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ───────── variant gallery ─────────
     One group per colour is rendered by Liquid (only for colours with the
     `custom.variant_pictures` metafield populated). The active colour's group
     is shown; everything else is hidden. Picking a colour that has no group
     hides the entire gallery block. */
  function bindVariantGalleryThumbs() {
    document.querySelectorAll('.variant-gallery__group').forEach(group => {
      const hero = group.querySelector('[data-variant-hero]');
      if (!hero) return;
      group.querySelectorAll('.variant-gallery__thumbs li').forEach(li => {
        li.addEventListener('click', () => {
          const heroSrc = li.dataset.heroSrc;
          if (heroSrc) hero.src = heroSrc;
          const img = li.querySelector('img');
          if (img) hero.alt = img.alt || hero.alt;
          group.querySelectorAll('.variant-gallery__thumbs li').forEach(x => x.removeAttribute('aria-current'));
          li.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  function paintVariantGallery() {
    if (!els.variantGalleries || els.variantGalleries.length === 0) return;
    const v = activeVariant();
    if (!v) return;
    const activeColor = optColor(v);
    els.variantGalleries.forEach(container => {
      let anyVisible = false;
      container.querySelectorAll('.variant-gallery__group').forEach(group => {
        const matches = group.dataset.variantColor === activeColor;
        group.hidden = !matches;
        if (matches) {
          anyVisible = true;
          // Reset hero to first thumbnail when swapping colours, so the user
          // always lands on the canonical first view.
          const firstThumb = group.querySelector('.variant-gallery__thumbs li');
          const hero = group.querySelector('[data-variant-hero]');
          if (firstThumb && hero && firstThumb.dataset.heroSrc) {
            hero.src = firstThumb.dataset.heroSrc;
            group.querySelectorAll('.variant-gallery__thumbs li').forEach(x => x.removeAttribute('aria-current'));
            firstThumb.setAttribute('aria-current', 'true');
          }
        }
      });
      container.hidden = !anyVisible;
    });
  }

  function bindControls() {
    if (els.prev) els.prev.addEventListener('click', () => shiftVariant(-1));
    if (els.next) els.next.addEventListener('click', () => shiftVariant(1));
    // Click on a neighbour vase navigates to it.
    els.vases.forEach(el => {
      el.addEventListener('click', () => {
        const role = el.dataset.role;
        if (role === 'left')  shiftVariant(-1);
        if (role === 'right') shiftVariant(1);
      });
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'pointer';
    });
    document.addEventListener('keydown', e => {
      if (e.target.matches('input, textarea, [contenteditable]')) return;
      if (e.key === 'ArrowLeft') shiftVariant(-1);
      if (e.key === 'ArrowRight') shiftVariant(1);
    });

    // Size pills (one block; rendered by Liquid, JS only handles clicks + state).
    if (els.sizePicker) {
      els.sizePicker.addEventListener('click', e => {
        const pill = e.target.closest('.size-pill');
        if (!pill || pill.disabled) return;
        setSize(pill.dataset.size);
      });
    }

    // Touch / swipe navigation across the viewer surface.
    // Mainly for mobile, but works on any touch device. Ignores swipes that
    // start on an interactive control (swatch, arrow, size pill, vase) so
    // those keep their own behaviour. Listeners are passive so vertical
    // movement still scrolls the page normally.
    if (els.viewer) {
      let touchStart = null;
      els.viewer.addEventListener('touchstart', e => {
        if (e.touches.length !== 1) { touchStart = null; return; }
        if (e.target.closest('button, a, input, .swatch, .size-pill, [data-vase]')) {
          touchStart = null;
          return;
        }
        const t = e.touches[0];
        touchStart = { x: t.clientX, y: t.clientY, t: Date.now() };
      }, { passive: true });

      els.viewer.addEventListener('touchend', e => {
        if (!touchStart) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStart.x;
        const dy = t.clientY - touchStart.y;
        const dt = Date.now() - touchStart.t;
        touchStart = null;

        // Heuristics: fast-ish horizontal swipe, ignore vertical (page scroll).
        if (dt > 700) return;
        if (Math.abs(dx) < 40) return;
        if (Math.abs(dy) > Math.abs(dx) * 0.8) return;

        if (dx < 0) shiftVariant(1);   // swipe left   → next vase
        else        shiftVariant(-1);  // swipe right  → previous vase
      }, { passive: true });

      els.viewer.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });
    }

    // Collapsible rows live in the main flow now (not nested in a sections wrapper).
    document.addEventListener('click', e => {
      const toggle = e.target.closest('.pdp-section__toggle');
      if (!toggle) return;
      const row = toggle.closest('.pdp-section');
      const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
      // Close siblings within main container only
      document.querySelectorAll('.pdp-section__toggle').forEach(t => {
        if (t !== toggle) t.setAttribute('aria-expanded', 'false');
      });
      toggle.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
    });

    if (els.qtyInc) els.qtyInc.addEventListener('click', () => setQty(state.qty + 1));
    if (els.qtyDec) els.qtyDec.addEventListener('click', () => setQty(state.qty - 1));
    if (els.qtyInput) els.qtyInput.addEventListener('change', () => setQty(parseInt(els.qtyInput.value, 10) || 1));

    els.rowArrows.forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.rowPrev || btn.dataset.rowNext;
        const dir = btn.dataset.rowNext ? 1 : -1;
        const row = document.querySelector(`[data-row="${key}"]`);
        if (!row) return;
        const step = Math.min(row.clientWidth * 0.8, 600);
        row.scrollBy({ left: step * dir, behavior: 'smooth' });
      });
    });
  }

  function setQty(v) {
    state.qty = Math.max(1, v);
    els.qtyInput.value = state.qty;
  }

  /* ───────── Concept variant-picker sync ───────── */

  // Listen for option changes inside the sibling featured-product section.
  // The Concept theme uses a <variant-selects> custom element with one radio
  // input per option value, with name="Color"/"Size" matching product.options.
  function bindConceptSync() {
    if (!els.conceptPicker) return;
    els.conceptPicker.addEventListener('change', e => {
      if (syncingToConcept) return;
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type !== 'radio') return;

      // Read every currently-checked option in the picker by option name.
      const selected = {};
      els.conceptPicker
        .querySelectorAll('input[type="radio"]:checked')
        .forEach(r => { selected[r.name] = r.value; });

      // Map the selected option values to a variant in our data.
      const variant = data.variants.find(v =>
        v.options.every((val, idx) => selected[data.options[idx]] === val)
      );
      if (variant) setVariant(variant.id, { fromConcept: true });
    });
  }

  // Drive the Concept picker so its colour / size selection mirrors ours.
  // Uses the guard above to break the change → change → change feedback loop.
  function syncToConcept() {
    if (!els.conceptPicker) return;
    const v = activeVariant();
    if (!v || !Array.isArray(v.options)) return;

    syncingToConcept = true;
    try {
      v.options.forEach((value, idx) => {
        const optName = data.options[idx];
        if (!optName) return;
        // Match the input by name + value. Escape both, since option values
        // can contain spaces or punctuation that break attribute selectors.
        const radios = els.conceptPicker.querySelectorAll(
          `input[type="radio"][name="${CSS.escape(optName)}"]`
        );
        radios.forEach(r => {
          if (r.value === value && !r.checked) {
            r.checked = true;
            r.dispatchEvent(new Event('input',  { bubbles: true }));
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    } finally {
      // Release on the next microtask so the events above finish bubbling
      // (Concept's own listeners may re-fire change during their handling).
      setTimeout(() => { syncingToConcept = false; }, 0);
    }
  }

  function bindForm() {
    if (!els.form) return;
    els.form.addEventListener('submit', async e => {
      e.preventDefault();
      const v = activeVariant();
      if (!v.available || els.cta.disabled) return;
      // Always read the latest quantity from the input — change event may not have fired
      // if the user typed and clicked without blurring.
      const qty = Math.max(1, parseInt(els.qtyInput && els.qtyInput.value, 10) || 1);
      state.qty = qty;
      if (els.qtyInput) els.qtyInput.value = qty;
      const variantId = state.variantId;
      els.cta.dataset.state = 'pending';
      els.ctaLabel.textContent = labels.adding;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
        });
        if (!res.ok) throw new Error(`Cart responded ${res.status}`);
        const result = await res.json();
        // /cart/add.js with `{items:[…]}` returns `{items:[…]}`; with a flat
        // `{id, quantity}` body it returns the single item directly. Handle both.
        const item = (result && Array.isArray(result.items)) ? result.items[0] : result;
        els.cta.dataset.state = 'added';
        els.ctaLabel.textContent = labels.added;
        const productTitle = (item && item.product_title) || data.title || '';
        const variantTitle = (item && item.variant_title) || (activeVariant() && activeVariant().title) || '';
        const toastParts = [productTitle, variantTitle].filter(s => s && s !== 'Default Title');
        showToast(`${qty} × ${toastParts.join(' · ')}`);
        try {
          const cart = await fetch('/cart.js').then(r => r.json());
          if (els.cartCount) els.cartCount.textContent = cart.item_count;
          // Concept theme cart count bubble: <cart-count hidden>{{ count }}</cart-count>
          // in snippets/header-icons.liquid. Update text + un-hide.
          document.querySelectorAll('cart-count').forEach(el => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) el.removeAttribute('hidden');
            else el.setAttribute('hidden', '');
          });
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          // Concept theme listens for these to refresh its cart drawer/header.
          // detail.open === true tells <cart-drawer> to slide open after refresh.
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: item }));
        } catch {}
        setTimeout(() => {
          els.cta.dataset.state = '';
          els.ctaLabel.textContent = v.available ? labels.add_to_cart : labels.sold_out;
        }, 2000);
      } catch (err) {
        console.error(err);
        els.cta.dataset.state = '';
        els.ctaLabel.textContent = labels.add_to_cart;
        showToast(labels.could_not);
      }
    });
  }

  /* ───────── recommendations ───────── */
  async function loadRecommendations() {
    const url = els.related.dataset.url;
    if (!url) return;
    try {
      const html = await fetch(url).then(r => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tiles = doc.querySelectorAll('[data-recommendations-tile]');
      const ul = els.related.querySelector('[data-row="related"]');
      if (!ul) return;
      tiles.forEach(t => ul.appendChild(t.cloneNode(true)));
      if (tiles.length === 0) els.related.remove();
    } catch (err) {
      console.warn('Recommendations failed', err);
      els.related.remove();
    }
  }

  /* ───────── recently viewed ───────── */
  function pushRecentlyViewed() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter(h => h !== data.handle);
      list.unshift(data.handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch {}
  }

  async function loadRecentlyViewed() {
    const ul = els.recent.querySelector('[data-row="recent"]');
    if (!ul) return;
    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter(h => h !== data.handle).slice(0, 6);
    } catch {}
    if (handles.length === 0) { els.recent.remove(); return; }
    const products = await Promise.all(handles.map(async h => {
      try { return await fetch(`/products/${h}.js`).then(r => r.json()); } catch { return null; }
    }));
    products.filter(Boolean).forEach(p => {
      const li = document.createElement('li');
      li.className = 'tile';
      li.innerHTML = `
        <a href="${p.url}">
          <div class="tile__media">
            <img src="${p.featured_image}" alt="${p.title}" loading="lazy" decoding="async">
          </div>
          <div class="tile__title"><span>${p.title}</span><span class="tile__price">${formatMoney(p.price)}</span></div>
        </a>`;
      ul.appendChild(li);
    });
    if (ul.children.length === 0) els.recent.remove();
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: data.price.currency }).format(cents / 100);
  }

  /* ───────── toast ───────── */
  let toastTimer;
  function showToast(msg) {
    if (!els.toast) return;
    els.toast.textContent = msg;
    els.toast.hidden = false;
    requestAnimationFrame(() => els.toast.dataset.visible = 'true');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      els.toast.dataset.visible = 'false';
      setTimeout(() => { els.toast.hidden = true; }, 320);
    }, 2400);
  }

  init();
})();

/* loader for separate lightbox module */
(function loadLightbox() {
  const scripts = document.getElementsByTagName('script');
  let base = '';
  for (let i = 0; i < scripts.length; i++) {
    const s = scripts[i];
    if (s.src && /pdp-nagami\.js/.test(s.src)) {
      base = s.src.replace(/pdp-nagami\.js[^/]*$/, '');
      break;
    }
  }
  const tag = document.createElement('script');
  tag.src = base + 'pdp-lightbox.js';
  tag.defer = true;
  document.head.appendChild(tag);
})();
